import { css, cx } from "@emotion/css";
import { Fade, SwipeableDrawer } from "@mui/material";
import { ChevronsLeftIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens, space, borderRadius, text } from "@octopusdeploy/design-system-tokens";
import React, { useEffect, useRef, useState } from "react";
import { useIsLargerThanIpadResolution } from "../../hooks/breakpoints";
import { isHTMLElement } from "../../utils";
import type { NavigationBarActionData } from "../NavigationBar/NavigationBarActions";
import { ScrollArea } from "../ScrollArea";
import { AreaNavList } from "./AreaNavList";
import type { AreaNavListItem } from "./AreaNavList";
import { SideNavBar, type BottomSideNavBarItem, type TopSideNavBarItem } from "./SideNavBar";
import { TopNavBar } from "./TopNavBar";
// These z-indexes are local to GlobalLayout through use of isolation css
const zIndexGlobalLayoutContent = 1;
const zIndexAreaNavPanel = 2;
export interface GlobalLayoutProps {
    topNavBar: {
        logo?: React.ReactElement;
        searchBar?: React.ReactElement;
        banner?: React.ReactElement;
        actions?: NavigationBarActionData[];
    };
    sideNavBar: {
        topItems: ReadonlyArray<TopSideNavBarItem>;
        bottomItems: ReadonlyArray<BottomSideNavBarItem>;
    };
    areaNavPanel: {
        items: ReadonlyArray<AreaNavListItem>;
        title?: string;
        expandState: AreaNavPanelExpandState;
        setExpandState: (reason: AreaNavPanelExpandState) => void;
        displayMode: "overlay" | "inline";
    };
    helpPanel: {
        isExpanded: boolean;
        content: React.ReactElement;
    };
    children: React.ReactNode;
    onSideBarClickAway?: () => void;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
export type AreaNavPanelExpandState = {
    isExpanded: true;
} | {
    isExpanded: true;
    reason: "hover";
    hoveredElement: HTMLElement;
} | {
    isExpanded: false;
};
export function GlobalLayout({ topNavBar, sideNavBar, areaNavPanel, onSideBarClickAway, children, scrollAreaRef, helpPanel }: GlobalLayoutProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const [isSideDrawerExpanded, setIsSideDrawerExpanded] = useState(false);
    const expandSideDrawer = () => setIsSideDrawerExpanded(true);
    const collapseSideDrawer = () => setIsSideDrawerExpanded(false);
    const mobileSideContainer = (<GlobalLayoutSideContainerDrawer isExpanded={isSideDrawerExpanded} onClose={collapseSideDrawer} onOpen={expandSideDrawer}>
            <SideNavBar topItems={sideNavBar.topItems} bottomItems={sideNavBar.bottomItems} onLinkClicked={collapseSideDrawer}/>
            <AreaNavPanel isCollapsible={false} listItems={areaNavPanel.items} title={areaNavPanel.title} onLinkClicked={collapseSideDrawer}/>
        </GlobalLayoutSideContainerDrawer>);
    const sideContainer = (<GlobalLayoutSideContainerFixed onClickAway={onSideBarClickAway}>
            <SideNavBar topItems={sideNavBar.topItems} bottomItems={sideNavBar.bottomItems} onLinkClicked={collapseSideDrawer}/>
            <AreaNavPanel isCollapsible={true} listItems={areaNavPanel.items} title={areaNavPanel.title} expandState={areaNavPanel.expandState} setExpandState={areaNavPanel.setExpandState} onLinkClicked={collapseSideDrawer} displayMode={areaNavPanel.displayMode}/>
        </GlobalLayoutSideContainerFixed>);
    return (<div className={rootProps}>
            <TopNavBar {...topNavBar} onExpandMenu={expandSideDrawer}/>
            <div className={mainContainerProps}>
                {isLargerThanIpad ? sideContainer : mobileSideContainer}
                <GlobalLayoutContent isAreaNavPanelExpandedInline={areaNavPanel.expandState.isExpanded && areaNavPanel.displayMode === "inline" && areaNavPanel.items.length > 0} isHelpPanelVisible={helpPanel.isExpanded} scrollAreaRef={scrollAreaRef}>
                    {children}
                </GlobalLayoutContent>
                {isLargerThanIpad && helpPanel.isExpanded && <GlobalLayoutHelpPanel>{helpPanel.content}</GlobalLayoutHelpPanel>}
            </div>
        </div>);
}
type GlobalLayoutSideContainerFixedProps = {
    onClickAway?: () => void;
    children: React.ReactNode;
};
type GlobalLayoutSideContainerDrawerProps = {
    isExpanded: boolean;
    onClose: () => void;
    onOpen: () => void;
    children: React.ReactNode;
};
function GlobalLayoutSideContainerFixed({ onClickAway, children }: React.PropsWithChildren<GlobalLayoutSideContainerFixedProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const container = containerRef.current;
        if (container === null)
            return;
        const onPointerDown = (event: FocusEvent | PointerEvent) => {
            if (isHTMLElement(event.target) && !container.contains(event.target) && onClickAway) {
                onClickAway();
            }
        };
        window.addEventListener("pointerdown", onPointerDown);
        return () => {
            window.removeEventListener("pointerdown", onPointerDown);
        };
    }, [onClickAway]);
    return (<div ref={containerRef} className={globalLayoutSideContainerFixedStyles}>
            {children}
        </div>);
}
function GlobalLayoutSideContainerDrawer({ isExpanded, onOpen, onClose, children }: React.PropsWithChildren<GlobalLayoutSideContainerDrawerProps>) {
    return (<SwipeableDrawer open={isExpanded} onClose={onClose} onOpen={onOpen}>
            <div className={globalLayoutSideContainerDrawerStyles}>{children}</div>
        </SwipeableDrawer>);
}
const globalLayoutSideContainerFixedStyles = css({
    display: "flex",
    position: "relative", // Needed when AreaNavBar goes into overlay/absolute mode to allow scrolling.
});
const globalLayoutSideContainerDrawerStyles = css({
    display: "flex",
    backgroundColor: themeTokens.color.background.secondary.default,
    height: "100%",
});
type AreaNavPanelProps = {
    listItems: ReadonlyArray<AreaNavListItem>;
    title?: string;
    onLinkClicked?: () => void;
} & ({
    isCollapsible: false;
} | {
    isCollapsible: true;
    expandState: AreaNavPanelExpandState;
    setExpandState: (expandState: AreaNavPanelExpandState) => void;
    displayMode: "overlay" | "inline";
});
function AreaNavPanel(props: AreaNavPanelProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mouseOutTimeout = useRef<number>();
    useEffect(() => {
        if (!props.isCollapsible || props.displayMode !== "overlay")
            return;
        const containerElement = containerRef.current;
        const hoverTriggerElement = props.expandState.isExpanded && "reason" in props.expandState && props.expandState.reason === "hover" ? props.expandState.hoveredElement : undefined;
        if (!hoverTriggerElement || !containerElement || !props.isCollapsible)
            return;
        const onMouseOver = (event: MouseEvent) => {
            if (isHTMLElement(event.target) && (containerElement.contains(event.target) || hoverTriggerElement.contains(event.target))) {
                window.clearTimeout(mouseOutTimeout.current);
                mouseOutTimeout.current = undefined;
            }
            else {
                if (!mouseOutTimeout.current) {
                    mouseOutTimeout.current = window.setTimeout(() => {
                        props.setExpandState({ isExpanded: false });
                    }, 200);
                }
            }
        };
        window.addEventListener("mouseover", onMouseOver);
        return () => {
            window.removeEventListener("mouseover", onMouseOver);
        };
    });
    if (props.listItems.length === 0) {
        return null;
    }
    if (!props.isCollapsible) {
        return (<div className={cx(areaNavPanelStyles, areaNavPanelRelativeStyles)}>
                {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
            </div>);
    }
    const { displayMode, expandState, setExpandState } = props;
    if (displayMode === "inline") {
        return (<div ref={containerRef} className={cx(areaNavPanelStyles, areaNavPanelRelativeStyles)}>
                <AreaNavPanelCollapsedToggle isPanelExpanded={expandState.isExpanded} onToggle={() => setExpandState(expandState.isExpanded ? { isExpanded: false } : { isExpanded: true })}/>
                {expandState.isExpanded && (<>
                        {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                        <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
                    </>)}
            </div>);
    }
    return (<Fade ref={containerRef} in={expandState.isExpanded}>
            <div className={cx(areaNavPanelStyles, areaNavPanelAbsoluteStyles)}>
                {props.title && <div className={areaNavPanelTitleStyles}>{props.title}</div>}
                <AreaNavList listItems={props.listItems} onLinkClicked={props.onLinkClicked} hasTitle={!!props.title}/>
            </div>
        </Fade>);
}
const toggleAreaNavPanelId = "toggleAreaNavPanel";
const areaNavPanelStyles = css({
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    boxShadow: themeTokens.shadow.layoutPrimary,
    borderTopLeftRadius: borderRadius.medium,
    overflow: "hidden",
    "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
    },
    [`:hover #${toggleAreaNavPanelId}`]: {
        opacity: "0.4",
        visibility: "visible",
    },
});
const areaNavPanelAbsoluteStyles = css({
    position: "absolute",
    left: "4.25rem",
    height: "100%",
    boxShadow: themeTokens.shadow.large,
    zIndex: zIndexAreaNavPanel, // This needs to show above the global layout content
    // Need a solid background when we're in absolute mode since we're overlaying other content.
    backgroundColor: themeTokens.color.background.primary.default,
});
const areaNavPanelRelativeStyles = css({
    position: "relative",
});
const areaNavPanelTitleStyles = css({
    font: text.heading.large,
    padding: `${space[32]} ${space[16]} 0 ${space[16]}`,
    background: themeTokens.color.background.primary.transparent,
});
interface GlobalLayoutContentProps {
    isAreaNavPanelExpandedInline: boolean;
    isHelpPanelVisible: boolean;
    scrollAreaRef: React.RefObject<HTMLDivElement | null>;
    children: React.ReactNode;
}
function GlobalLayoutContent({ isAreaNavPanelExpandedInline, isHelpPanelVisible, scrollAreaRef, children }: GlobalLayoutContentProps) {
    return (<main className={cx(globalLayoutContentStyles, {
            [globalLayoutContentWithoutAreaNavPanelStyles]: !isAreaNavPanelExpandedInline,
            [globalLayoutContentWithHelpPanelStyles]: isHelpPanelVisible,
        })}>
            <ScrollArea scrollAreaRef={scrollAreaRef}>{children}</ScrollArea>
        </main>);
}
interface AreaNavPanelCollapsedToggleProps {
    isPanelExpanded: boolean;
    onToggle: () => void;
}
function AreaNavPanelCollapsedToggle({ isPanelExpanded, onToggle }: AreaNavPanelCollapsedToggleProps) {
    const handleToggle = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle();
    }, [onToggle]);
    return (<div id={toggleAreaNavPanelId} className={toggleStyles} onClick={handleToggle}>
            {isPanelExpanded && <ChevronsLeftIcon size={16}/>}
        </div>);
}
const toggleStyles = css({
    position: "absolute",
    opacity: "0",
    visibility: "hidden",
    transition: "opacity 300ms",
    transitionTimingFunction: "ease-in",
    right: space[16],
    top: space[12],
    cursor: "pointer",
    ".MuiIconButton-root": {
        color: themeTokens.color.icon.secondary,
    },
});
function GlobalLayoutHelpPanel({ children }: {
    children: React.ReactNode;
}) {
    return <div className={globalLayoutHelpPanelStyles}>{children}</div>;
}
const globalLayoutHelpPanelStyles = css({
    position: "relative",
    display: "flex",
    boxShadow: themeTokens.shadow.layoutPrimary,
    background: themeTokens.color.background.supplementary.default,
    marginLeft: space[16],
    borderTopLeftRadius: borderRadius.medium,
});
const globalLayoutContentStyles = css({
    flex: 1,
    isolation: "isolate",
    boxShadow: themeTokens.shadow.layoutPrimary,
    background: themeTokens.color.background.supplementary.default,
    overflow: "clip",
    // Prevents content from overflowing the width of this container
    minWidth: 0,
    // There is some content in portal that shows a fullscreen dialog/overlay e.g. the new project dialog
    // Without this z-index, the help panel has higher natural stacking order and appears over this content
    zIndex: zIndexGlobalLayoutContent,
});
const globalLayoutContentWithoutAreaNavPanelStyles = css({
    borderTopLeftRadius: borderRadius.medium,
});
const globalLayoutContentWithHelpPanelStyles = css({
    borderTopRightRadius: borderRadius.medium,
});
const rootProps = css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: themeTokens.color.background.secondary.default,
});
const mainContainerProps = css({
    flex: 1,
    display: "flex",
    minHeight: 0,
    isolation: "isolate",
});
